'use client';
import { proPath } from '@/lib/proUrl.mjs'; // Founder 06/10/26: a pro's URL is ONLY its number
import { agreeAdjPlural } from '@/lib/hePluralAgree.mjs';
import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useFitAvatar } from '@/lib/useFitAvatar';
import { useSmartIntakeDemo } from '@/lib/smartIntakeStore';
import ReviewsList, { type ReviewItem } from './ReviewsList';
import { SINGULAR } from './SmartIntakeBar';
import { avatarImg } from '@/lib/imgUrl';
import { ProCardSkeleton, SkeletonStyles } from './Skeletons';

// Heading grammar (bughunter 2026-09-27: "חשמלאי מומלצים"): the plural from the shared singular map; construct forms that
// already read plural ("טכנאי מזגנים", "שמאי מקרקעין") stay; a service name ("סגירת מרפסת זכוכית") becomes "מומחי …".
const PLURAL: Record<string, string> = Object.fromEntries(Object.entries(SINGULAR).map(([pl, sg]) => [sg, pl]));
const EXTRA_PLURAL: Record<string, string> = { 'קבלן': 'קבלנים', 'קבלן שיפוצים': 'קבלני שיפוצים', 'קבלן אלומיניום': 'קבלני אלומיניום' };
function pluralTitle(name: string): string {
  if (PLURAL[name]) return PLURAL[name];
  if (EXTRA_PLURAL[name]) return EXTRA_PLURAL[name];
  if (/^חברת /.test(name)) return name.replace(/^חברת /, 'חברות ');
  const first = name.split(' ')[0];
  if (name.includes(' ') && /י$/.test(first)) return name;
  if (/(ים|ות)$/.test(first)) return name;
  return `מומחי ${name}`;
}

const HE_ROW: React.CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.35, marginBottom: 2 };
type RealPro = {
  id: number; name: string; slug: string; city: string | null; rating: number; reviewCount: number;
  image: string | null; categorySlug: string; categoryName: string | null; reviews: ReviewItem[];
};

// "איך זה עובד?" — 3D transparent icons from the icon engine (scripts/pricing-icons.mjs, prefix home-), Founder 2026-09-27.
const TRUST_ITEMS = [
  { icon: 'home-rank', title: 'מוצאים לפי דירוג', text: 'כל בעל מקצוע מדורג על ידי לקוחות אמיתיים. הגבוה ביותר עולה ראשון.' },
  { icon: 'home-verified', title: 'המלצות מאומתות', text: 'כל המלצה מאומתת ב-SMS. אין המלצות מזויפות.' },
  { icon: 'home-message', title: 'הודעת וואטסאפ מוכנה', text: 'שולחים הודעה לבעל המקצוע כבר עם פרטי הבקשה שלכם.' },
  { icon: 'home-local', title: 'המומחה הכי טוב באזור', text: 'מסננים לפי עיר ואזור. הדירוג הגבוה ביותר בסביבתכם.' },
  { icon: 'home-time', title: 'חוסכים זמן', text: 'כתבו מה אתם צריכים, ה-AI מוצא בשבילכם.' },
  { icon: 'home-top9', title: <>רק מומחים עם דירוג <bdi dir="ltr">4.5+</bdi> (מתוך 5 כוכבים)</>, text: 'הדירוג המקורי מגוגל, בלי שום עיבוד.' },
];

// Same masonry + endless-scroll (up/down/sideways) reviews engine used on the category
// page's professional cards (components/ReviewsList.tsx) — one shared engine, not a
// separate simplified version for the homepage. The whole card links through to the
// professional's own page (avatar, name and the "view profile" row are all wrapped).
function ProCard({ pro, priority = false }: { pro: RealPro; priority?: boolean }) {
  // Founder 2026-09-28 (screenshot, דוד/איילין): the photo grows into the card's own empty padding (extra 22px, offset by a
  // negative margin) — bigger face, same card height.
  const fitAv = useFitAvatar(62, { min: 62, max: 120, extra: 22 });
  const singularCat = pro.categoryName || pro.categorySlug.replace(/-/g, ' ');
  const href = proPath(pro.id);
  return (
    <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,.08)', border: '1.5px solid #e8edf5', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Link href={href} style={{ padding: '16px 16px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
        {pro.image ? (
          <img {...avatarImg(pro.image, 120, { kind: 'pro', ctx: { category: pro.categoryName, proName: pro.name } })} referrerPolicy="no-referrer" loading={priority ? 'eager' : 'lazy'} decoding="async" {...(priority ? { fetchPriority: 'high' as const } : {})}
            style={{ width: fitAv.size, height: fitAv.size, margin: fitAv.measured ? '-11px 0' : 0, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid #e0e7ff' }} />
        ) : (
          <div style={{ width: fitAv.size, height: fitAv.size, margin: fitAv.measured ? '-11px 0' : 0, borderRadius: '50%', flexShrink: 0, background: '#0d47a1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 900, boxShadow: '0 3px 10px rgba(13,71,161,.25)' }}>
            {pro.name[0]}
          </div>
        )}
        <div ref={fitAv.textRef} style={{ flex: 1, minWidth: 0 }}>
          {/* Founder 2026-09-29: five fixed one-line rows (name · category · city · stars · count) — nothing wraps, so every card
              has the same text height and the text-fitted avatar stays the same size on every card. */}
          <div style={{ ...HE_ROW, fontSize: 18, fontWeight: 900, color: '#0f172a', lineHeight: 1.25 }}>{pro.name}</div>
          <div style={{ ...HE_ROW, fontSize: 13.5, color: '#64748b', fontWeight: 600 }}>{singularCat}</div>
          <div style={{ ...HE_ROW, fontSize: 13.5, color: '#64748b', fontWeight: 600, minHeight: '1.35em' }}>{pro.city || ''}</div>
          <div style={{ ...HE_ROW, display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ color: '#f59e0b', fontSize: 14 }}>
              {[1,2,3,4,5].map(i => i <= Math.round(pro.rating) ? '★' : '☆').join('')}
            </span>
            <span style={{ fontSize: 15, fontWeight: 900, color: '#0d47a1' }}><bdi dir="ltr">{pro.rating.toFixed(1)}</bdi></span>
          </div>
          <div style={{ ...HE_ROW, fontSize: 12.5, color: '#94a3b8', fontWeight: 500 }}><bdi dir="ltr">{pro.reviewCount}</bdi> המלצות</div>
        </div>
      </Link>
      {pro.reviews.length > 0 && (
        <>
          <div style={{ borderTop: '1px solid #f1f5f9', margin: '0 16px' }} />
          <div style={{ padding: '10px 16px 14px' }}>
            <ReviewsList reviews={pro.reviews} proId={pro.id} totalCount={pro.reviewCount} />
          </div>
        </>
      )}
      <Link href={href} style={{ display: 'block', textAlign: 'center', padding: '10px 16px', fontSize: 13, fontWeight: 800, color: '#0d47a1', borderTop: '1px solid #f1f5f9' }}>
        צפה בפרופיל ←
      </Link>
    </div>
  );
}

export default function HomeExperts({ initial }: { initial?: { slug: string | null; name: string | null; city: string | null; scope: string; plural?: string | null; pros: RealPro[] } } = {}) {
  // Shared smart-intake subscriber (one poller app-wide — see lib/smartIntakeStore)
  // 30/09/26 (Founder: "the page loads, refreshes, then loads OTHER experts"): the list is the server-rendered set until the
  // visitor TYPES — then their words drive it (skeletons while the new set loads). The idle bar's rotating example and a stored
  // context from an earlier visit never swap it.
  const demo = useSmartIntakeDemo();
  const ctx = demo?.typed ? demo : null;
  const initialKey = initial ? `${initial.slug}|${initial.city}` : null;
  const [mounted, setMounted] = useState(false);
  const [pros, setPros] = useState<RealPro[]>(initial?.pros || []);
  const [shown, setShown] = useState<{ slug: string | null; name: string | null; city: string | null; scope: string; plural?: string | null }>(initial ? { slug: initial.slug, name: initial.name, city: initial.city, scope: initial.scope, plural: initial.plural ?? null } : { slug: null, name: null, city: null, scope: 'top' });
  const [gen, setGen] = useState(0); // bumps on every new result set → the cards replay their gentle entrance
  const [loading, setLoading] = useState(!initial);
  // the set already on screen (SSR first set, 2026-09-30) is never fetched again; any other category/city is
  const lastKey = useRef<string | null>(initial ? `${initial.slug}|${initial.city}` : null);
  useEffect(() => { setMounted(true); }, []);

  const slug = ctx?.categorySlug || null;
  const city = ctx?.city || null;
  const name = ctx?.categoryName || null;

  // Always real data, always live — no cached/placeholder fallback. Refetches whenever
  // the smart-intake bar resolves a different category/city from the user's free text.
  useEffect(() => {
    if (!mounted) return;
    const key = ctx ? `${slug}|${city}` : (initialKey ?? 'null|null');
    if (key === lastKey.current) return;
    lastKey.current = key;
    if (initial && key === initialKey) { // back to the server set (the visitor cleared the query) — already in hand, no fetch
      setPros(initial.pros); setShown({ slug: initial.slug, name: initial.name, city: initial.city, scope: initial.scope, plural: initial.plural ?? null });
      setGen(g => g + 1); setLoading(false); return;
    }
    let cancelled = false;
    setLoading(true);
    const sp = new URLSearchParams({ limit: '6' });
    if (slug) sp.set('category', slug);
    if (city) sp.set('city', city);
    fetch(`/api/home-experts?${sp}`)
      .then(r => r.json())
      // skeleton cards (same box) hold the grid until the new set arrives — then the real cards replay their entrance
      .then(d => { if (cancelled) return; setPros(d.pros || []); setShown({ slug, name, city, scope: d.scope || 'top', plural: d.categoryPlural ?? null }); setGen(g => g + 1); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mounted, slug, city, !!ctx]); // eslint-disable-line react-hooks/exhaustive-deps

  // reserve roughly the final height before hydration (bughunter: CLS 0.21 from a 400px placeholder growing to ~1000px)
  if (!mounted && !initial) return <div className="he-reserve"><style>{`.he-reserve{min-height:900px}@media(min-width:900px){.he-reserve{min-height:1180px}}`}</style></div>;

  // The title describes what is REALLY on screen: a category with no pro in that city says so instead of claiming the city.
  const catLabel = shown.slug && shown.name ? (shown.plural || pluralTitle(shown.name)) : null; // DB plural (lib/categoryPlural) first
  const local = shown.scope === 'city' || shown.scope === 'city+';
  const title = catLabel
    ? `${catLabel} ${agreeAdjPlural(catLabel, 'מומלצים')}${shown.city && local ? ` ב${shown.city}` : ''}`
    : shown.city && local ? `מומחים מומלצים ב${shown.city}` : 'מומחים מומלצים';

  return (
    <div dir="rtl">
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px 8px' }}>
        <h1 key={`t${gen}`} className="he-fadein" style={{ fontSize: 20, fontWeight: 900, color: 'var(--sk-head, #0f172a)', margin: '0 0 16px', textAlign: 'center' }}>{title}</h1>
        {!loading && pros.length === 0 ? null : loading ? (
          <div className="he-grid" aria-busy="true" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 14 }}>
            <SkeletonStyles />
            {Array.from({ length: Math.max(4, Math.min(pros.length, 6)) }, (_, i) => <ProCardSkeleton key={i} />)}
          </div>
        ) : (
          <div className="he-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 14 }}>
            {pros.map((p, i) => (
              <div key={`${gen}-${p.id}`} className={gen ? 'he-cardin' : undefined /* the SSR first set paints at once (LCP) — only later sets replay the entrance */} style={{ animationDelay: `${Math.min(i, 5) * 60}ms` }}>
                <ProCard pro={p} priority={i < 2} />
              </div>))}
          </div>
        )}
        <style>{`
          @keyframes heCardIn { from { opacity: 0; transform: translateY(10px) scale(.985); } to { opacity: 1; transform: none; } }
          @keyframes heFadeIn { from { opacity: 0; } to { opacity: 1; } }
          .he-cardin { animation: heCardIn .42s cubic-bezier(.2,.7,.2,1) both; display: flex; flex-direction: column; }
          .he-cardin > div { flex: 1; }
          .he-grid { min-height: 760px; }
          @media (min-width: 900px) { .he-grid { min-height: 700px; } }
          .he-how { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
          @media (max-width: 560px) { .he-how { grid-template-columns: repeat(2, 1fr); gap: 10px; } }
          .he-fadein { animation: heFadeIn .35s ease both; }
          @media (prefers-reduced-motion: reduce) { .he-cardin, .he-fadein { animation: none; } }
        `}</style>
      </div>
      <div style={{ background: 'var(--sk-band, linear-gradient(170deg, #f0f9ff 0%, #f8fafc 100%))', borderTop: '1px solid var(--sk-line, #e0f2fe)', borderBottom: '1px solid var(--sk-line, #e0f2fe)', margin: '20px 0 0', padding: '24px 16px 20px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <h3 style={{ textAlign: 'center', fontSize: 16, fontWeight: 900, color: 'var(--sk-head, #0f172a)', marginBottom: 18, marginTop: 0 }}>איך זה עובד?</h3>
          <div className="he-how">
            {TRUST_ITEMS.map(item => (
              <div key={item.icon} style={{ background: '#fff', borderRadius: 12, padding: '13px 12px', border: '1.5px solid #e0f2fe', boxShadow: '0 1px 6px rgba(13,71,161,.05)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 6 }}>
                <img {...avatarImg(`/img/pricing/${item.icon}.webp`, 56)} alt="" aria-hidden width={56} height={56} loading="lazy" decoding="async" style={{ width: 56, height: 56, objectFit: 'contain' }} />
                <div style={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a', lineHeight: 1.3 }}>{item.title}</div>
                <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>{item.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
